import useGetCustomer from '@/api/queries/customers/useGetCustomer';
import useGetCustomers from '@/api/queries/customers/useGetCustomers';
import { type ComboboxProps } from '@payaca/ambrosia';
import { useDebounceCallback } from '@payaca/hooks';
import { useMemo, useState } from 'react';

/**
 * The Combobox props a Customer search drives. Derived from `ComboboxProps`,
 * so a change to the control's contract fails to compile here rather than at
 * each call site.
 */
export type CustomerSearchProps = Pick<
  ComboboxProps<number, false>,
  'options' | 'query' | 'setQuery' | 'filterFunction'
>;

/** A Customer the caller can already name, so the label costs no fetch. */
export type KnownCustomer = { id: number; name: string };

const SEARCH_DEBOUNCE_MS = 300;
const PAGE_SIZE = 20;

/**
 * Stands in for the options while a search is in flight. Combobox renders an
 * empty list as an empty dropdown, which reads as "no customers", and offers
 * no loading state of its own.
 */
const SEARCHING_OPTION = { value: -1, label: 'Searching…', disabled: true };

/**
 * Turns any Combobox into a Customer search: spread the result onto the
 * control and leave `value` / `onChange` to whatever owns the selection.
 * `useCustomerSearchField` does that binding for react-hook-form.
 *
 * This is the only place that knows where Customers come from — swapping the
 * data layer is a change to this file alone.
 */
export const useCustomerSearch = (params: {
  selectedCustomerId: number | null;
  /**
   * Skips the fetch that labels the current selection, where the caller has
   * the name already. Ignored once the selection moves off this Customer.
   */
  knownCustomer?: KnownCustomer;
}): CustomerSearchProps => {
  const { selectedCustomerId, knownCustomer } = params;

  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const debounceQuery = useDebounceCallback(
    setDebouncedQuery,
    SEARCH_DEBOUNCE_MS
  );

  const { customers, isFetching } = useGetCustomers(
    { limit: PAGE_SIZE, offset: 0 },
    { searchTerm: debouncedQuery || undefined }
  );

  // A search stops returning the selection as soon as the query moves off it,
  // and a Combobox cannot label an option it does not hold.
  const isKnown = knownCustomer?.id === selectedCustomerId;
  const { customer: fetchedCustomer } = useGetCustomer(
    selectedCustomerId != null && !isKnown
      ? String(selectedCustomerId)
      : undefined
  );

  const options = useMemo<CustomerSearchProps['options']>(() => {
    const found = (customers?.items ?? []).map((customer) => ({
      value: Number(customer.id),
      label: customer.name,
    }));

    if (!found.length && isFetching) return [SEARCHING_OPTION];

    const selectedLabel = isKnown ? knownCustomer.name : fetchedCustomer?.name;
    if (
      selectedCustomerId != null &&
      selectedLabel &&
      !found.some((option) => option.value === selectedCustomerId)
    ) {
      found.push({ value: selectedCustomerId, label: selectedLabel });
    }

    return found;
  }, [
    customers,
    isFetching,
    isKnown,
    knownCustomer,
    fetchedCustomer,
    selectedCustomerId,
  ]);

  return {
    options,
    query,
    setQuery: (next) => {
      setQuery(next);
      debounceQuery(next);
    },
    // Filtering is server-side.
    filterFunction: null,
  };
};
